import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { Plus } from 'lucide-react'
import PropertyCard from '../components/customer/PropertyCard'
import PropertyForm, { buildPropertyFormValues, type PropertyFormValues } from '../components/customer/PropertyForm'
import Button from '../components/ui/Button'
import FormError from '../components/ui/FormError'
import {
  CREATE_PROPERTY_MUTATION,
  DELETE_PROPERTY_MUTATION,
  MY_PROPERTIES_QUERY,
  UPDATE_PROPERTY_MUTATION,
  type CreatePropertyData,
  type CreatePropertyInput,
  type CreatePropertyVariables,
  type CustomerProperty,
  type DeletePropertyData,
  type DeletePropertyVariables,
  type MyPropertiesData,
  type UpdatePropertyData,
  type UpdatePropertyInput,
  type UpdatePropertyVariables,
} from '../graphql/customer'
import { useLang, useScopedTranslations } from '../contexts/LangContext'

const translations = {
  el: {
    eyebrow: 'Ακίνητα',
    title: 'Οργάνωσε τις διευθύνσεις που χρησιμοποιείς στις κρατήσεις',
    body: 'Αποθήκευσε ακίνητα, πρόσβαση και βασικές λεπτομέρειες ώστε κάθε νέα κράτηση να γίνεται πιο γρήγορα.',
    loading: 'Φορτώνουμε τα ακίνητά σου...',
    emptyTitle: 'Δεν έχεις προσθέσει ακίνητα ακόμη',
    emptyBody: 'Πρόσθεσε την πρώτη διεύθυνση για να τη χρησιμοποιείς σε νέες κρατήσεις.',
    add: 'Προσθήκη ακινήτου',
    addAnother: 'Νέο ακίνητο',
    saveError: 'Δεν ήταν δυνατή η αποθήκευση του ακινήτου.',
    deleteError: 'Δεν ήταν δυνατή η διαγραφή του ακινήτου.',
    savedCreate: 'Το ακίνητο προστέθηκε.',
    savedUpdate: 'Το ακίνητο ενημερώθηκε.',
    deleted: 'Το ακίνητο διαγράφηκε.',
    total: 'ενεργά ακίνητα',
  },
  en: {
    eyebrow: 'Properties',
    title: 'Organize the addresses you reuse in bookings',
    body: 'Save properties, access details, and the basics so each new booking takes less effort.',
    loading: 'Loading your properties...',
    emptyTitle: 'No properties added yet',
    emptyBody: 'Add your first address so you can reuse it in future bookings.',
    add: 'Add property',
    addAnother: 'New property',
    saveError: 'Could not save the property.',
    deleteError: 'Could not delete the property.',
    savedCreate: 'Property added.',
    savedUpdate: 'Property updated.',
    deleted: 'Property deleted.',
    total: 'active properties',
  },
} as const

function toInput(values: PropertyFormValues): CreatePropertyInput {
  return {
    label: values.label.trim(),
    propertyType: values.propertyType,
    addressLine1: values.addressLine1.trim(),
    addressLine2: values.addressLine2.trim() || undefined,
    city: values.city.trim() || 'Athens',
    postalCode: values.postalCode.replace(/\D/g, '').slice(0, 5),
    region: values.region.trim() || undefined,
    country: 'GR',
    floor: values.floor ? Number(values.floor) : undefined,
    hasElevator: values.hasElevator,
    sizeSqm: values.sizeSqm ? Number(values.sizeSqm) : undefined,
    bedrooms: values.bedrooms ? Number(values.bedrooms) : undefined,
    bathrooms: values.bathrooms ? Number(values.bathrooms) : undefined,
    accessInstructions: values.accessInstructions.trim() || undefined,
    notes: values.notes.trim() || undefined,
    isDefault: values.isDefault,
  }
}

function toFormValues(property: CustomerProperty | null) {
  if (!property) return buildPropertyFormValues()

  return buildPropertyFormValues({
    label: property.label,
    propertyType: property.propertyType,
    addressLine1: property.addressLine1,
    addressLine2: property.addressLine2 ?? '',
    city: property.city,
    postalCode: property.postalCode,
    region: property.region ?? '',
    floor: property.floor?.toString() ?? '',
    sizeSqm: property.sizeSqm?.toString() ?? '',
    bedrooms: property.bedrooms?.toString() ?? '',
    bathrooms: property.bathrooms?.toString() ?? '',
    accessInstructions: property.accessInstructions ?? '',
    notes: property.notes ?? '',
    hasElevator: property.hasElevator,
    isDefault: property.isDefault,
  })
}

export default function PropertiesPage() {
  const { locale, t } = useLang()
  const copy = useScopedTranslations('properties', translations[locale])

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null)
  const [selectedProperty, setSelectedProperty] = useState<CustomerProperty | null>(null)
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [pageMessage, setPageMessage] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { data, loading, refetch } = useQuery<MyPropertiesData>(MY_PROPERTIES_QUERY)
  const [createProperty, { loading: creating }] = useMutation<CreatePropertyData, CreatePropertyVariables>(CREATE_PROPERTY_MUTATION)
  const [updateProperty, { loading: updating }] = useMutation<UpdatePropertyData, UpdatePropertyVariables>(UPDATE_PROPERTY_MUTATION)
  const [deleteProperty] = useMutation<DeletePropertyData, DeletePropertyVariables>(DELETE_PROPERTY_MUTATION)

  const properties = data?.myProperties ?? []
  const saving = creating || updating
  const initialValues = useMemo(() => toFormValues(selectedProperty), [selectedProperty])

  async function handleSubmit(values: PropertyFormValues) {
    setFormErrors([])
    setPageMessage(null)

    try {
      if (formMode === 'edit' && selectedProperty) {
        const input: UpdatePropertyInput = { id: selectedProperty.id, ...toInput(values) }
        const { data } = await updateProperty({ variables: { input } })
        const errors = data?.updateProperty.errors ?? [copy.saveError]
        if (errors.length > 0) {
          setFormErrors(errors)
          return
        }

        setPageMessage(copy.savedUpdate)
      } else {
        const { data } = await createProperty({ variables: { input: toInput(values) } })
        const errors = data?.createProperty.errors ?? [copy.saveError]
        if (errors.length > 0) {
          setFormErrors(errors)
          return
        }

        setPageMessage(copy.savedCreate)
      }

      setFormMode(null)
      setSelectedProperty(null)
      await refetch()
    } catch {
      setFormErrors([copy.saveError])
    }
  }

  async function handleDelete(property: CustomerProperty) {
    if (!window.confirm(t('Να διαγραφεί αυτό το ακίνητο;', 'Delete this property?'))) return

    setFormErrors([])
    setPageMessage(null)
    setDeletingId(property.id)

    try {
      const { data } = await deleteProperty({ variables: { id: property.id } })
      const errors = data?.deleteProperty.errors ?? [copy.deleteError]
      if (errors.length > 0 || !data?.deleteProperty.success) {
        setFormErrors(errors)
        return
      }

      if (selectedProperty?.id === property.id) {
        setSelectedProperty(null)
        setFormMode(null)
      }
      setPageMessage(copy.deleted)
      await refetch()
    } catch {
      setFormErrors([copy.deleteError])
    } finally {
      setDeletingId(null)
    }
  }

  function openCreateForm() {
    setFormErrors([])
    setPageMessage(null)
    setSelectedProperty(null)
    setFormMode('create')
  }

  function openEditForm(property: CustomerProperty) {
    setFormErrors([])
    setPageMessage(null)
    setSelectedProperty(property)
    setFormMode('edit')
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">{copy.eyebrow}</p>
          <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">{copy.title}</h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">{copy.body}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-600">
            {properties.length} {copy.total}
          </p>
          <Button type="button" onClick={openCreateForm}>
            <Plus className="h-4 w-4" />
            {properties.length === 0 ? copy.add : copy.addAnother}
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <FormError errors={formErrors} />
        {pageMessage && (
          <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
            {pageMessage}
          </div>
        )}
      </div>

      {formMode && (
        <div className="mt-8">
          <PropertyForm
            mode={formMode}
            initialValues={initialValues}
            loading={saving}
            errors={formErrors}
            onSubmit={handleSubmit}
            onCancel={() => {
              setFormMode(null)
              setSelectedProperty(null)
              setFormErrors([])
            }}
          />
        </div>
      )}

      {loading && properties.length === 0 ? (
        <p className="mt-8 text-sm text-slate-500">{copy.loading}</p>
      ) : properties.length === 0 ? (
        <div className="mt-8 rounded-[1.75rem] border border-slate-200 bg-slate-50 p-6">
          <p className="text-2xl font-bold text-slate-950">{copy.emptyTitle}</p>
          <p className="mt-3 text-sm leading-7 text-slate-600">{copy.emptyBody}</p>
          {!formMode && (
            <div className="mt-6">
              <Button type="button" onClick={openCreateForm}>{copy.add}</Button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {properties.map((property) => (
            <PropertyCard
              key={property.id}
              property={property}
              deleting={deletingId === property.id}
              onEdit={() => openEditForm(property)}
              onDelete={() => handleDelete(property)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
